import { useEffect, useRef, useState } from "react";
import { SiteCopy, siteData } from "../copy";
import { HERO_CYCLE, heroCycleStart, heroCycleTick } from "./heroCycle";

/**
 * The story's opening line (prototype v36/v38): the phrase from `home.story.heroCycle` before a
 * fixed gold "simplified.", backspaced and retyped on one rAF loop (timing in heroCycle.ts).
 *
 * It only runs while the first chapter is readable: the story is on screen (IntersectionObserver
 * on the sticky block), the tab is visible and chapter 0 is not fading. Otherwise it parks on a
 * whole phrase and backs off to a 400 ms timer. Under `prefers-reduced-motion` it renders
 * `home.story.s1` and nothing moves. The CSS holds the line's height (`min-height`, `32ch`), so
 * nothing below it moves as the words change.
 */
export function HeroLine() {
  const list = siteData<string[]>("home.story.heroCycle");
  const reduce = useReduceMotion();
  const ref = useRef<HTMLHeadingElement>(null);
  const [text, setText] = useState(list[0]);

  useEffect(() => {
    const h1 = ref.current;
    if (reduce || !h1 || list.length < 2) return;
    const ch = h1.closest<HTMLElement>(".st-ch");
    const stick = h1.closest<HTMLElement>(".st-stick");
    const ready = () => {
      if (document.hidden) return false;
      // Unset means the story hasn't painted yet (or isn't running): readable.
      const o = parseFloat(ch?.style.opacity ?? "");
      return !(o >= 0 && o < 0.9);
    };
    let s = heroCycleStart(performance.now());
    let raf = 0;
    let idle = 0;
    let on = false;
    let shown = list[0];
    const show = (t: string) => {
      if (t !== shown) setText((shown = t));
    };
    const tick = (now: number) => {
      raf = 0;
      idle = 0;
      if (!on) return;
      const r = heroCycleTick(s, now, list, ready());
      s = r.s;
      show(r.text);
      if (r.idle) {
        idle = window.setTimeout(() => {
          idle = 0;
          if (on) tick(performance.now());
        }, HERO_CYCLE.idle);
      } else raf = requestAnimationFrame(tick);
    };
    const start = () => {
      if (on) return;
      on = true;
      s = heroCycleStart(performance.now());
      show(list[0]);
      raf = requestAnimationFrame(tick);
    };
    const stop = () => {
      on = false;
      cancelAnimationFrame(raf);
      clearTimeout(idle);
      raf = 0;
      idle = 0;
    };
    let io: IntersectionObserver | null = null;
    if (stick && "IntersectionObserver" in window) {
      io = new IntersectionObserver((es) =>
        es.forEach((e) => (e.isIntersecting ? start() : stop())),
      );
      io.observe(stick);
    }
    start();
    return () => {
      stop();
      io?.disconnect();
    };
  }, [reduce, list]);

  if (reduce) return <SiteCopy as="h1" k="home.story.s1" />;
  return (
    <h1 ref={ref} data-c="home.story.s1">
      <span className="hcpre">{text}</span> <span className="st-g">simplified.</span>
    </h1>
  );
}

function useReduceMotion(): boolean {
  const [reduce] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );
  return reduce;
}
